import React, { useState } from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import MenuIcon from "@mui/icons-material/Menu";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

const Header: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  const menuItems = [
    { key: "about", path: "/", label: t("navigation.about") },
    {
      key: "research",
      path: "/research-and-articles",
      label: t("navigation.research"),
    },
    { key: "topics", path: "/#topics-section", label: t("navigation.topics") },
    { key: "fees", path: "/#fees", label: t("navigation.fees") },
    { key: "contact", path: "/#contact", label: t("navigation.contact") },
  ];

  const handleSearchOpen = () => {
    if (isMobile) {
      // On mobile, navigate to dedicated search page
      navigate("/search");
    } else {
      // On desktop, show modal overlay
      setSearchOpen(true);
    }
  };
  const handleSearchClose = () => {
    setSearchOpen(false);
    setSearchValue("");
  };
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSearchValue(e.target.value);
  const handleSearchSubmit = (e: React.FormEvent | React.MouseEvent) => {
    e.preventDefault();
    if (searchValue.trim()) {
      navigate(
        `/research-and-articles?search=${encodeURIComponent(
          searchValue.trim()
        )}`
      );
      setSearchOpen(false);
      setSearchValue("");
    }
  };

  const drawer = (
    <List>
      {menuItems.map((item) => (
        <ListItem
          component={item.path.includes("#") ? HashLink : RouterLink}
          to={item.path}
          key={item.key}
          onClick={handleDrawerToggle}
          sx={{ cursor: "pointer" }}
        >
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              sx: {
                color:
                  (item.key === "about" && location.pathname === "/") ||
                  (item.key !== "about" && location.pathname === item.path)
                    ? "#1976d2"
                    : "#000",
                fontWeight: 500,
                fontSize: "18px",
                fontFamily: "Roboto, Helvetica, Arial, sans-serif",
              },
            }}
          />
        </ListItem>
      ))}
      <ListItem>
        <Button
          onClick={() => changeLanguage("en")}
          sx={{
            mr: 1,
            fontSize: "18px",
            fontFamily: "Roboto, Helvetica, Arial, sans-serif",
            color: i18n.language === "en" ? "#1976d2" : "#000",
            fontWeight: 500,
            background: "none",
            boxShadow: "none",
            minWidth: 0,
            px: 1.5,
          }}
        >
          EN
        </Button>
        <Button
          onClick={() => changeLanguage("ru")}
          sx={{
            fontSize: "18px",
            fontFamily: "Roboto, Helvetica, Arial, sans-serif",
            color: i18n.language === "ru" ? "#1976d2" : "#000",
            fontWeight: 500,
            background: "none",
            boxShadow: "none",
            minWidth: 0,
            px: 1.5,
          }}
        >
          RU
        </Button>
      </ListItem>
      <ListItem sx={{ justifyContent: "flex-start" }}>
        <IconButton onClick={handleSearchOpen}>
          <SearchIcon sx={{ fontSize: "2rem", color: "inherit" }} />
        </IconButton>
      </ListItem>
    </List>
  );

  return (
    <>
      <AppBar position="static" sx={{ background: "#29505c" }}>
        <Toolbar
          sx={{
            justifyContent: "space-between",
            minHeight: { xs: 104, md: 124 },
          }}
        >
          {/* Logo */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box
              component={RouterLink}
              to="/"
              sx={{ display: "flex", alignItems: "center", mr: 2 }}
            >
              <Box
                component="img"
                src="/images/psy-logo.png"
                alt="Logo"
                sx={{
                  height: { xs: 64, md: 124 },
                  width: { xs: 64, md: "auto" },
                  maxHeight: { xs: 64, md: 124 },
                  maxWidth: { xs: 64, sm: 110, md: 140, lg: 180 },
                }}
              />
            </Box>
          </Box>

          {/* Desktop Menu */}
          {!isMobile && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexGrow: 1,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexGrow: 1,
                  minWidth: 0,
                  overflow: "auto",
                }}
              >
                {menuItems.map((item) => (
                  <Button
                    key={item.key}
                    component={item.path.includes("#") ? HashLink : RouterLink}
                    to={item.path}
                    color="inherit"
                    sx={{
                      mx: { xs: 0.2, sm: 0.5, md: 0.8 },
                      fontWeight: 400,
                      fontSize: {
                        xs: "10px",
                        sm: "11px",
                        md: "12px",
                        lg: "14px",
                      },
                      color:
                        (item.key === "about" && location.pathname === "/") ||
                        (item.key !== "about" &&
                          location.pathname === item.path)
                          ? "#fff"
                          : "#6ec6e6",
                      fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexShrink: 0,
                  justifyContent: "flex-end",
                  minWidth: 0,
                  pl: 2,
                }}
              >
                <IconButton
                  color="inherit"
                  onClick={handleSearchOpen}
                  sx={{ ml: 2 }}
                >
                  <SearchIcon sx={{ fontSize: "2rem", color: "#fff " }} />
                </IconButton>
                <Button
                  onClick={() => changeLanguage("en")}
                  sx={{
                    ml: { xs: 0.5, sm: 1, md: 2, lg: 3 },
                    mr: 0.2,
                    fontSize: {
                      xs: "10px",
                      sm: "11px",
                      md: "12px",
                      lg: "14px",
                    },
                    fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                    color: i18n.language === "en" ? "#fff" : "#6ec6e6",
                    fontWeight: 500,
                    background: "none",
                    boxShadow: "none",
                    minWidth: 0,
                    px: 1.5,
                  }}
                >
                  EN
                </Button>
                <Button
                  onClick={() => changeLanguage("ru")}
                  sx={{
                    fontSize: {
                      xs: "10px",
                      sm: "11px",
                      md: "12px",
                      lg: "14px",
                    },
                    fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                    color: i18n.language === "ru" ? "#fff" : "#6ec6e6",
                    fontWeight: 500,
                    background: "none",
                    boxShadow: "none",
                    minWidth: 0,
                    px: 1.5,
                  }}
                >
                  RU
                </Button>
              </Box>
            </Box>
          )}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="end"
              onClick={handleDrawerToggle}
              sx={{ ml: 1 }}
            >
              <MenuIcon sx={{ fill: "#505865", fontSize: "2.5rem" }} />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { boxSizing: "border-box", width: 240 },
        }}
      >
        {drawer}
      </Drawer>
      {/* Search Overlay */}
      {searchOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "20vh",

            bgcolor: "rgba(41, 80, 92, 0.98)",
            zIndex: 1400,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            pt: { xs: 4, md: 5 },
          }}
        >
          {
            <Box
              component="form"
              onSubmit={handleSearchSubmit}
              sx={{
                display: "flex",
                alignItems: "center",
                width: { xs: "90vw", sm: 500, md: 700 },
                bgcolor: "#fff",
                borderRadius: 1,
                boxShadow: 3,
                p: 0,
                position: "relative",
              }}
            >
              <input
                autoFocus
                type="text"
                placeholder="Search..."
                value={searchValue}
                onChange={handleSearchChange}
                style={{
                  flex: 1,
                  border: "none",
                  outline: "none",
                  fontSize: 24,
                  padding: "24px 24px 24px 24px",
                  borderRadius: 4,
                  background: "#fff",
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") handleSearchClose();
                }}
              />
              <Box sx={{ display: "flex", alignItems: "center", height: 48 }}>
                <IconButton
                  type="submit"
                  sx={{
                    bgcolor: "#20414a",
                    color: "#fff",
                    borderRadius: 0,
                    height: 75.5,
                    width: 75.5,
                    "&:hover": { bgcolor: "#1976d2" },
                  }}
                >
                  <SearchIcon sx={{ fontSize: 32 }} />
                </IconButton>
              </Box>
              <IconButton
                onClick={handleSearchClose}
                sx={{
                  position: "absolute",
                  right: "-7vw",
                  color: "#fff",
                  bgcolor: "transparent",
                  p: 1,
                  height: 48,
                  width: 48,
                }}
              >
                <CloseIcon sx={{ fontSize: 32 }} />
              </IconButton>
            </Box>
          }
        </Box>
      )}
    </>
  );
};

export default Header;
